<script lang="ts" setup>
import { ref, computed } from "vue";
import { useFetch } from "#app";
import type { IProvider } from "~/types/IProvider";

definePageMeta({
  layout: "default",
});

const { t } = useI18n();
const runtimeConfig = useRuntimeConfig();
const folderLink = runtimeConfig.public.folderLink || '';
const searchQuery = ref('');

// Fonction pour obtenir l'URL complète du logo
function getLogoUrl(provider: IProvider): string {
  if (provider.logo) {
    return folderLink + provider.logo;
  }
  return '';
}

// Récupération de tous les providers
const { data: providers, error, pending } = await useFetch('/api/providers/getAllProviders', {
  method: 'GET',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Fonction pour filtrer les providers basée sur la recherche
const filteredProviders = computed(() => {
  if (!providers.value?.providers) return [];
  
  const providersList = providers.value.providers as IProvider[];
  
  if (!searchQuery.value.trim()) {
    return providersList;
  }
  
  return providersList.filter((provider: IProvider) =>
    provider.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    (provider.description && provider.description.toLowerCase().includes(searchQuery.value.toLowerCase()))
  );
});

// Fonction pour gérer le clic sur un provider
function handleProviderClick(provider: IProvider) {
  if (provider.link) {
    window.open(provider.link, '_blank', 'noopener,noreferrer');
  }
}
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <div class="bg-white shadow">
      <div class="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
        <div class="text-center mt-12">
          <h1 class="text-4xl font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
            {{ t('providers.title') }}
          </h1>
          <p class="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
            {{ t('providers.description') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Search Section -->
    <div class="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div class="max-w-md mx-auto">
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <UIcon name="i-heroicons-magnifying-glass" class="h-5 w-5 text-gray-400" />
          </div>
          <UInput
            v-model="searchQuery"
            :placeholder="t('providers.search-placeholder')"
            size="lg"
            class="w-full pl-10"
          />
        </div>
      </div>
    </div>

    <div v-if="pending" class="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div class="text-center">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-greenCharlys-30"></div>
        <p class="mt-2 text-gray-600">{{ t('providers.loading') }}</p>
      </div>
    </div>

    <div v-else-if="error" class="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div class="text-center">
        <div class="text-red-500 text-lg">{{ t('providers.error') }}</div>
      </div>
    </div>

    <div v-else class="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div v-if="filteredProviders.length === 0" class="text-center py-12">
        <div class="text-gray-500 text-lg">
          {{ searchQuery ? t('providers.no-results') : t('providers.no-providers') }}
        </div>
      </div>

      <!-- Providers grid -->
      <div v-else class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <div
          v-for="provider in filteredProviders"
          :key="provider._id"
          class="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 cursor-pointer group"
          @click="handleProviderClick(provider)"
        >
          <div class="p-6">
            <div class="flex justify-center mb-4">
              <img
                v-if="provider.logo"
                :src="getLogoUrl(provider)"
                :alt="provider.name"
                class="h-24 w-24 object-contain"
                @error="($event.target as HTMLImageElement).style.display='none'"
              />
              
              <div v-else class="h-24 w-24 bg-gray-100 flex items-center justify-center rounded-lg">
                <span class="text-gray-400 text-2xl font-bold">
                  {{ provider.name.charAt(0).toUpperCase() }}
                </span>
              </div>
            </div>

            <h3 class="text-lg font-semibold text-gray-900 text-center mb-2 group-hover:text-greenCharlys-30 transition-colors">
              {{ provider.name }}
            </h3>

            <p v-if="provider.description" class="text-gray-600 text-sm text-center line-clamp-3">
              {{ provider.description }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="h-16"></div>
  </div>
</template>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  line-clamp: 3;
  overflow: hidden;
}
</style>
